import type { GeraeteSpeicher } from '../geraetespeicher';
import type { RetoureAbschluss, RetoureAnfrage } from './api';

/**
 * Retourenvorgang auf dem Gerät (N1-Muster, Sicherheitskonzept 4.1): Die Vorgangs-ID wird
 * **vor dem ersten Senden** dauerhaft im Gerätespeicher abgelegt (IndexedDB `bilanx-kassa`,
 * Bereich `meta`, Schlüssel `retoure|<Kassen-ID>`). Je Kasse gibt es höchstens einen offenen
 * Retourenvorgang. Eine Wiederholung (Netzabbruch, Neuladen, Klärung) trägt immer dieselbe ID;
 * eine neue ID entsteht nur für einen neuen Vorgang.
 *
 * Phasen:
 * - `gesendet`: Anfrage unterwegs bzw. Ausgang unbekannt – nicht auszahlen, nur abfragen.
 * - `abgeschlossen`: Retourenbeleg signiert (Ergebnis gespeichert). Lade und Druck erst jetzt,
 *   jeweils höchstens einmal (Merker vor dem Auslösen).
 * Abgelehnte Vorgänge werden entfernt (der Server hat nichts gebucht).
 *
 * Fenster (Codex R1 K1/K2): Ein Vorgang gehört genau einem Fenster (`fenster`, Kennung je Reiter in
 * sessionStorage, übersteht Neuladen). Andere Fenster zeigen ihn nur an. Übernehmen geht erst, wenn
 * kein Abschluss mehr laufen kann (`sendetBis` vorbei); jede Aktion (Senden, Lade, Auszahlung,
 * Abbrechen, Fertig) prüft den Besitz in derselben Transaktion.
 */
export interface RetoureVorgangDatensatz {
  vorgangId: string;
  kassenId: string;
  angelegtAm: string;
  /** Auswahl wie gesendet (Wiederholung mit denselben Nutzdaten, Vertrag 3.1). */
  anfrage: RetoureAnfrage;
  /** Gerätekennung beim ersten Senden (Wiederholung mit derselben Kennung). */
  geraetekennung: string;
  phase: 'gesendet' | 'abgeschlossen';
  /** Fenster, das den Vorgang führt (fehlt bei alten Datensätzen = übernehmbar). */
  fenster?: string;
  /** Bis hierhin kann ein Abschluss noch unterwegs sein (ms seit Epoche): kein Übernehmen, kein Abbruch. */
  sendetBis?: number;
  ergebnis?: RetoureAbschluss;
  /** Kassenlade für diesen Vorgang schon angefordert (nie ein zweites Mal automatisch). */
  ladeAngefordert?: true;
  /** Retourenbeleg schon zum Druck gegeben. */
  druckAngefordert?: true;
  /** Auszahlung von der Kassierkraft bestätigt (Geld übergeben). */
  auszahlungBestaetigt?: true;
  /** Bestätigung der Auszahlung am Server vermerkt (`POST …/auszahlung`). */
  auszahlungGemeldet?: true;
}

const FENSTER_SCHLUESSEL = 'bilanx.retoureFenster';
let fensterImSpeicher: string | null = null;

/** Kennung dieses Fensters (Reiters); bleibt beim Neuladen gleich. */
export function fensterKennung(): string {
  try {
    const da = sessionStorage.getItem(FENSTER_SCHLUESSEL);
    if (da !== null && da !== '') {
      return da;
    }
    const neu = crypto.randomUUID();
    sessionStorage.setItem(FENSTER_SCHLUESSEL, neu);
    return neu;
  } catch {
    fensterImSpeicher ??= crypto.randomUUID();
    return fensterImSpeicher;
  }
}

function gehoert(d: RetoureVorgangDatensatz, fenster: string): boolean {
  return d.fenster === undefined || d.fenster === fenster;
}

function schluessel(kassenId: string): string {
  return `retoure|${kassenId}`;
}

export function neueVorgangId(): string {
  return crypto.randomUUID();
}

export async function ladeRetoureVorgang(
  speicher: GeraeteSpeicher,
  kassenId: string,
): Promise<RetoureVorgangDatensatz | null> {
  return speicher.transaktion(
    async (tx) => (await tx.get<RetoureVorgangDatensatz>('meta', schluessel(kassenId))) ?? null,
  );
}

/**
 * Vorgang vor dem Senden sichern. Liegt schon einer vor, bleibt er unverändert und wird
 * zurückgegeben (dieselbe ID, auch wenn zwei Fenster gleichzeitig bestätigen).
 */
export async function sichereRetoureVorgang(
  speicher: GeraeteSpeicher,
  neu: RetoureVorgangDatensatz,
): Promise<RetoureVorgangDatensatz> {
  return speicher.transaktion(async (tx) => {
    const vorhanden = await tx.get<RetoureVorgangDatensatz>('meta', schluessel(neu.kassenId));
    if (vorhanden !== undefined) {
      return vorhanden;
    }
    await tx.put('meta', schluessel(neu.kassenId), neu);
    return neu;
  });
}

/**
 * Felder eines vorhandenen Vorgangs ergänzen (nur derselbe Vorgang und, mit `fenster`, nur im
 * eigenen Fenster; sonst nichts und null).
 */
export async function aktualisiereRetoureVorgang(
  speicher: GeraeteSpeicher,
  kassenId: string,
  vorgangId: string,
  teil: Partial<Omit<RetoureVorgangDatensatz, 'vorgangId' | 'kassenId'>>,
  fenster?: string,
): Promise<RetoureVorgangDatensatz | null> {
  return speicher.transaktion(async (tx) => {
    const d = await tx.get<RetoureVorgangDatensatz>('meta', schluessel(kassenId));
    if (d?.vorgangId !== vorgangId || (fenster !== undefined && !gehoert(d, fenster))) {
      return null;
    }
    const neu = { ...d, ...teil };
    await tx.put('meta', schluessel(kassenId), neu);
    return neu;
  });
}

/**
 * Merker atomar setzen; true nur für den ersten Aufrufer (Lade bzw. Druck genau einmal, auch
 * über zwei Fenster und nach Neuladen).
 */
export async function beanspruche(
  speicher: GeraeteSpeicher,
  kassenId: string,
  vorgangId: string,
  merker: 'ladeAngefordert' | 'druckAngefordert' | 'auszahlungBestaetigt',
  fenster: string,
): Promise<boolean> {
  return speicher.transaktion(async (tx) => {
    const d = await tx.get<RetoureVorgangDatensatz>('meta', schluessel(kassenId));
    if (
      d?.vorgangId !== vorgangId ||
      d.phase !== 'abgeschlossen' ||
      d[merker] === true ||
      !gehoert(d, fenster)
    ) {
      return false;
    }
    await tx.put('meta', schluessel(kassenId), { ...d, [merker]: true });
    return true;
  });
}

/** Vorgang beenden (nach „Fertig“ bzw. endgültiger Ablehnung) – nur genau diese ID und nur im eigenen Fenster. */
export async function entferneRetoureVorgang(
  speicher: GeraeteSpeicher,
  kassenId: string,
  vorgangId: string,
  fenster: string,
): Promise<boolean> {
  return speicher.transaktion(async (tx) => {
    const d = await tx.get<RetoureVorgangDatensatz>('meta', schluessel(kassenId));
    if (d?.vorgangId === vorgangId && gehoert(d, fenster)) {
      await tx.delete('meta', schluessel(kassenId));
      return true;
    }
    return false;
  });
}

/**
 * Vorgang in dieses Fenster übernehmen – nur, wenn kein Abschluss mehr unterwegs sein kann
 * (`sendetBis` vorbei). Liefert den Datensatz bzw. null.
 */
export async function uebernimmRetoureVorgang(
  speicher: GeraeteSpeicher,
  kassenId: string,
  vorgangId: string,
  fenster: string,
  jetzt: number,
): Promise<RetoureVorgangDatensatz | null> {
  return speicher.transaktion(async (tx) => {
    const d = await tx.get<RetoureVorgangDatensatz>('meta', schluessel(kassenId));
    if (d?.vorgangId !== vorgangId) {
      return null;
    }
    if (d.fenster === fenster) {
      return d;
    }
    if ((d.sendetBis ?? 0) > jetzt) {
      return null;
    }
    const neu = { ...d, fenster };
    await tx.put('meta', schluessel(kassenId), neu);
    return neu;
  });
}

/**
 * Codex R2 K1: Eine Kennung aus sessionStorage allein unterscheidet keine lebenden Fenster (ein
 * duplizierter Reiter kopiert sie). Jedes Fenster hält daher einen Web Lock auf seine Kennung,
 * solange es offen ist; ist die Kennung schon gesperrt, nimmt das Fenster eine neue.
 *
 * Codex R3 K1: Ohne nutzbare Web Locks (alte Browser, Fehler) gilt eine flüchtige Kennung nur für
 * diese Seite – nicht in sessionStorage, also auch nicht kopierbar. Nach einem Neuladen gehört der
 * Vorgang dann einem „anderen Fenster“ und muss ausdrücklich übernommen werden (nur ohne
 * laufenden Abschluss); sicher statt bequem.
 */
const fensterVersprechen = new Map<string, Promise<string>>();

export function fensterBeanspruchen(): Promise<string> {
  const start = fensterKennung();
  let v = fensterVersprechen.get(start);
  if (v === undefined) {
    v = fensterSperren(start);
    fensterVersprechen.set(start, v);
  }
  return v;
}

async function fensterSperren(start: string): Promise<string> {
  // Ältere Browser bzw. Testumgebungen liefern undefined oder null (kein Web Lock)
  const locks =
    typeof navigator === 'undefined'
      ? undefined
      : (navigator as { locks?: LockManager | null }).locks;
  if (locks === undefined || locks === null) {
    return crypto.randomUUID();
  }
  let id = start;
  for (let versuch = 0; versuch < 3; versuch++) {
    const name = `bilanx-retoure-fenster:${id}`;
    // null: Sperre nicht nutzbar (Fehler) → Startkennung behalten
    const erhalten = await new Promise<boolean | null>((fertig) => {
      Promise.resolve()
        .then(() =>
          locks.request(name, { ifAvailable: true }, (lock) => {
            if (lock === null) {
              fertig(false);
              return undefined;
            }
            fertig(true);
            // bis das Fenster geschlossen wird
            return new Promise<never>(() => undefined);
          }),
        )
        .catch(() => {
          fertig(null);
        });
    });
    if (erhalten === null) {
      return crypto.randomUUID();
    }
    if (erhalten) {
      return id;
    }
    id = crypto.randomUUID();
    try {
      sessionStorage.setItem(FENSTER_SCHLUESSEL, id);
    } catch {
      fensterImSpeicher = id;
    }
  }
  // dreimal belegt: nicht raten, flüchtige Kennung
  return crypto.randomUUID();
}

/**
 * Codex R2 M4: Entwurf vor dem Abschluss (Vorgangs-ID, Original, Auswahl, Grund) – vor der ersten
 * Vorschau gespeichert, damit ein Neuladen dieselbe Vorgangs-ID fortsetzt. Wird mit dem Sichern
 * des Abschlussvorgangs bzw. „Neue Retoure“ entfernt.
 */
export interface RetoureEntwurf<W> {
  vorgangId: string;
  original: { kassenId: string; belegnummer: string };
  auswahl: [number, W][];
  grund: string | null;
  grundText: string;
  schritt: string;
}

function entwurfSchluessel(kassenId: string): string {
  return `retoure-entwurf|${kassenId}`;
}

export async function sichereRetoureEntwurf<W>(
  speicher: GeraeteSpeicher,
  kassenId: string,
  entwurf: RetoureEntwurf<W>,
): Promise<void> {
  await speicher.transaktion(async (tx) => {
    await tx.put('meta', entwurfSchluessel(kassenId), entwurf);
  });
}

export async function ladeRetoureEntwurf<W>(
  speicher: GeraeteSpeicher,
  kassenId: string,
): Promise<RetoureEntwurf<W> | null> {
  return speicher.transaktion(
    async (tx) => (await tx.get<RetoureEntwurf<W>>('meta', entwurfSchluessel(kassenId))) ?? null,
  );
}

export async function entferneRetoureEntwurf(
  speicher: GeraeteSpeicher,
  kassenId: string,
): Promise<void> {
  await speicher.transaktion(async (tx) => {
    await tx.delete('meta', entwurfSchluessel(kassenId));
  });
}
